import { CreditCard, Pencil, Receipt } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { PetDiscount, PortalPlan, RegionId, SubscriptionPlan } from '@snootsvet/shared/portal'

import {
  DISCOUNT_LABELS,
  describePlanPrice,
} from '@/customer/components/billing/subscription-plans'
import { Card, CardAction, CardContent, CardHeader } from '@/customer/components/ui/card'

/**
 * Read-only echo of the plan the customer picked, shown on the
 * checkout page below the You/Pet confirmation cards.
 *
 * Uses the same card chrome + icon-prefixed row idiom as the You and
 * Pet cards so the three sit together as a visual set.
 * Yellow fill echoes the picker's "selected" state - a subtle nod
 * that this is the plan the customer just chose, without turning
 * the card into a re-sell (no perks, no savings, no bounce).
 *
 * Returns `null` for a plan `plans` doesn't include; the checkout page
 * redirects to the picker before that can happen.
 */
export function SelectedSubscriptionPlanCard({
  plan,
  region,
  plans,
  discount,
  editHref,
}: {
  plan: SubscriptionPlan
  region: RegionId
  /** Live prices from `GET /plans`. */
  plans: readonly PortalPlan[]
  /** The discount the checkout applies to `plan`, if any. */
  discount?: PetDiscount
  /**
   * When set, renders a small pencil in the card header that links
   * back to the plan picker so the customer can change their mind
   * without leaving the flow.
   */
  editHref?: string
}) {
  const name = plans.find((p) => p.plan === plan)?.name
  const pricing = describePlanPrice(plans, plan, region, discount?.percentOff)
  if (!name || !pricing) return null

  return (
    <Card color="green" className="bg-green/60">
      <CardHeader>
        <h3 className="text-headline-sm">Selected plan</h3>
        {editHref ? (
          <CardAction>
            <Link
              to={editHref}
              aria-label="Change plan"
              className="inline-flex p-1 text-foreground/70 transition-colors hover:text-foreground"
            >
              <Pencil className="size-4" />
            </Link>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="text-sm font-bold gap-y-2">
        <p className="flex items-center gap-2">
          <CreditCard className="size-4 shrink-0" />
          <span>
            {name} &middot; {pricing.price}/mo
          </span>
        </p>
        <p className="flex items-center gap-2">
          <Receipt className="size-4 shrink-0" />
          <span>
            {pricing.billing}
            {discount ? `, with ${discount.percentOff}% ${DISCOUNT_LABELS[discount.kind]}` : null}
          </span>
        </p>
      </CardContent>
    </Card>
  )
}
